import { Theme, ThemeProvider } from '@emotion/react'
import PlayerCore, { videojs } from '@mycujoo/player-core'
import {
  Props as PlayerCoreProps,
  VideoJSExtended,
} from '@mycujoo/player-core/dist/components/VideoJS'
import {
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import youboraAdapter from 'youbora-adapter-videojs'
import youbora from 'youboralib'
import { version } from '../../package.json'
import { useOnPlayerInit, usePlayerState } from '../contexts/playerState'
import { Ad, AdsManager } from '../utils/google.ima'
import { Midroll, useAdManagerTracking } from '../utils/hooks'
import theme from '../utils/theme'
import CustomControls from './CustomControls'
import InjectedOverlays from './InjectedOverlays'
import PlayerOverlay from './PlayerOverlay'
import TotalViewers from './TotalViewers'
import Annotations from './annotations'

export interface MLSPlayerProps extends PlayerCoreProps {
  // essential
  source: string
  // flags
  autoplay?: boolean
  debug?: boolean
  showBackForwardButtons?: boolean
  showLiveViewers?: boolean
  showInfoButton?: boolean
  showSeekbar?: boolean
  showFullscreen?: boolean
  showQualitySelector?: boolean
  showVolume?: boolean
  showTimers?: boolean
  showChromecast?: boolean
  showPictureInPicture?: boolean
  enableAnnotations?: boolean
  showLiveUI?: boolean
  showMarkers?: boolean
  // values
  primaryColor?: string
  infoContent?: ReactNode
  defaultVolume?: number
  startPosition?: number
  chromecastCustomData?: {
    eventId: string
    publicKey: string
    identityToken: string
    customPlaylistUrl: string
    pseudoUserId: string
    receiverAppID: string
  }
  posterUrl?: string
  auth?: string
  drm?: {
    fairplay?: {
      certificate_url: string
      full_url: string
      license_url: string
    }
    playready?: {
      full_url: string
      license_url: string
    }
    widevine?: {
      full_url: string
      license_url: string
    }
  }
  // thrird parties
  youboraOptions?: {
    accountCode: string
    'content.isLive': boolean
  } | null
  midrolls?: Midroll[]
  googimaTag?: string
  muxOptions?: {
    envKey: string
  }
  // handlers
  onAdsManager?: (adsManager: GoogleImaAdsManager) => void
  onAdsBreakStart?: () => void
  onAdStart?: (ad: GoogleImaAd | null) => void
  onAdSkip?: () => void
  onAdComplete?: () => void
  onAdsBreakComplete?: () => void
  onFullscreenChange?: (isFullscreen: boolean) => void
}

export type GoogleImaAdsManager = AdsManager
export type GoogleImaAd = Ad

type VjsPlugin = (_?: Record<string, unknown>) => void

interface IMAPlugin extends VjsPlugin {
  initializeAdDisplayContainer: () => void
}
type MuxPlugin = VjsPlugin
export interface PlayerExtended extends VideoJSExtended {
  // plugins
  ads: VjsPlugin
  ima: IMAPlugin
  youbora: unknown
  mux: MuxPlugin
}

const Player: FunctionComponent<MLSPlayerProps> = (props) => {
  const {
    source,
    autoplay,
    debug,
    onClick,
    onPlayerReady,
    onError,
    onPlayerActive,
    onPlay,
    onPause,
    onEnd,
    onFirstPlay,
    onSeeked,
    onSeeking,
    onWaiting,
    onDispose,
    onLoadedMetadata,
    onQualityChange,
    onTimeUpdate,
    onAdsManager,
    onAdsBreakStart,
    onAdStart,
    onAdSkip,
    onAdComplete,
    onAdsBreakComplete,
    onFullscreenChange,

    children,
    defaultVolume,
    primaryColor,
    chromecastCustomData,
    showQualitySelector,
    showBackForwardButtons,
    showLiveViewers,
    showInfoButton,
    showSeekbar,
    showFullscreen,
    showVolume,
    showTimers,
    showChromecast,
    showPictureInPicture,
    showLiveUI,
    showMarkers,
    startPosition,
    muxOptions,
    googimaTag,
    infoContent,
    youboraOptions,
    enableAnnotations,
    posterUrl,
    auth,
    drm,
    midrolls,
  } = props
  const youboraPlugin = useRef<any | null>(null)
  const {
    playerInstance,
    totalViewers,
    isFullscreen,
    isLive,
    isCasting,
    isInitialized,
  } = usePlayerState()
  const onPlayerInitialize = useOnPlayerInit()

  const handleContainerClick = useCallback(() => {
    onClick?.()
    googimaTag && playerInstance?.ima.initializeAdDisplayContainer()
  }, [googimaTag, onClick, playerInstance?.ima])

  const onReady = useCallback(() => {
    debug && videojs.log('🎥 Player-MLS ready', playerInstance)
    onPlayerReady?.()
  }, [debug, onPlayerReady, playerInstance])

  const onPlayerInit = useCallback(
    (videoJsPlayer: VideoJSExtended): void => {
      debug && videojs.log('🎥 initialize stateStore', { videoJsPlayer })

      onPlayerInitialize(videoJsPlayer as PlayerExtended)

      startPosition && videoJsPlayer.currentTime(startPosition)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startPosition],
  )

  useEffect(() => {
    if (onFullscreenChange) {
      onFullscreenChange(!!isFullscreen)
    }
  }, [onFullscreenChange, isFullscreen])

  useEffect(() => {
    youboraPlugin.current?.setOptions({
      ...youboraOptions,
      'content.isLive': isLive,
    })
  }, [isLive, youboraOptions])

  useEffect(() => {
    isCasting
      ? youboraPlugin.current?.disable()
      : youboraPlugin.current?.enable()
  }, [isCasting, youboraOptions])

  useEffect(() => {
    debug && videojs.log(`🎥 player-mls@${version}`)

    videojs.log.level(props.debug ? 'all' : 'off')

    debug && videojs.log('🎥 Player-MLS props:', props)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    playerInstance &&
      !youboraPlugin.current &&
      (async () => {
        if (
          !playerInstance ||
          playerInstance?.isDisposed() ||
          youboraPlugin.current
        ) {
          return
        }
      console.log("YOUBORA OPTIONS: ", youboraOptions)
        if (youboraOptions) {
          debug &&
            videojs.log('Loading Youbora', youboraOptions, playerInstance)
          try {
            youbora.Log.logLevel = debug
              ? youbora.Log.Level.DEBUG
              : youbora.Log.Level.ERROR
            youboraPlugin.current = new youbora.Plugin({
              accountCode: youboraOptions.accountCode,
            })

            youboraPlugin.current?.setOptions(youboraOptions)

            youboraPlugin.current?.setAdapter(
              new youboraAdapter(playerInstance),
            )
          } catch (error) {
            videojs.log('❌ Failed to load Youbora', error)
          }
        }
      console.log("MUX OPTIONS: ", muxOptions, playerInstance?.mux)
        if (muxOptions) {
          debug && videojs.log('Loading MUX', muxOptions, playerInstance)
          try {
            playerInstance?.mux({
              debug,
              data: {
                env_key: muxOptions.envKey,
                ...muxOptions,
                player_name: 'MCLS Player',
                player_version: '1.1.x',
                player_init_time: new Date(),
              },
            })
          } catch (error) {
            videojs.log('❌ Failed to load Mux', error)
          }
        }
      })()
  }, [
    debug,
    muxOptions,
    playerInstance,
    youboraOptions,
    youboraPlugin,
  ])

  useEffect(() => {
    let inPast = false

    const onAdStartHandler = () => {
      const notAtLive = !playerInstance?.liveTracker.atLiveEdge()
      inPast = notAtLive
    }

    const onAdEnd = () => {
      if (playerInstance?.isDisposed()) return

      playerInstance?.play()
      if (playerInstance?.currentTime() === 0 || !inPast) {
        playerInstance?.liveTracker.isLive() &&
          playerInstance?.liveTracker.seekToLiveEdge()
      }
    }

    if (googimaTag) {
      debug && videojs.log('Loading IMA', googimaTag, playerInstance?.ima)
      try {
        playerInstance?.ima({
          adTagUrl: googimaTag,
          contribAdsSettings: { allowVjsAutoplay: true },
          debug,
        })
        playerInstance?.on('adend', onAdEnd)
        playerInstance?.on('adstart', onAdStartHandler)
      } catch (error) {
        videojs.log('❌ Failed to load IMA', error)
      }
    }
    return () => {
      playerInstance?.off('adend', onAdEnd)
      playerInstance?.off('adstart', onAdStartHandler)
    }
  }, [debug, googimaTag, playerInstance])

  useAdManagerTracking(
    playerInstance,
    onAdsManager,
    onAdsBreakStart,
    onAdStart,
    onAdSkip,
    onAdComplete,
    onAdsBreakComplete,
    midrolls,
  )

  debug &&
    videojs.log('🎥 Player-MLS render', {
      isPlayerInitialized: isInitialized,
      source,
    })

  return (
    <ThemeProvider
      theme={(rootTheme: Theme) => ({
        ...theme,
        ...rootTheme,
        colors: {
          ...theme.colors,
          primary: primaryColor || theme.colors.primary,
        },
      })}
    >
      <PlayerCore
        playsInline={!isFullscreen}
        source={source}
        autoplay={autoplay}
        volume={defaultVolume}
        debug={debug}
        onClick={handleContainerClick}
        onError={onError}
        onPlayerReady={onReady}
        onPlayerActive={onPlayerActive}
        onPlay={onPlay}
        onFirstPlay={onFirstPlay}
        onPause={onPause}
        onEnd={onEnd}
        onWaiting={onWaiting}
        onSeeked={onSeeked}
        onSeeking={onSeeking}
        onPlayerInit={onPlayerInit}
        onTimeUpdate={onTimeUpdate}
        onDispose={onDispose}
        onLoadedMetadata={onLoadedMetadata}
        onQualityChange={onQualityChange}
        showQualitySelector={showQualitySelector}
        showSeekbar={showSeekbar}
        showFullscreen={showFullscreen}
        showVolume={showVolume}
        showTimers={showTimers}
        showChromecast={showChromecast}
        showPictureInPicture={showPictureInPicture}
        chromecastCustomData={chromecastCustomData}
        posterUrl={posterUrl}
        showLiveUI={showLiveUI}
        auth={auth}
        drm={drm}
      >
        {enableAnnotations && (
          <Annotations
            showSeekbar={showSeekbar ?? true}
            showMarkers={showMarkers ?? true}
          />
        )}
        <CustomControls
          showTimers={showTimers ?? true}
          showSeekbar={showSeekbar ?? true}
          showLiveUI={showLiveUI ?? true}
        />
        {showLiveViewers && <TotalViewers totalViewers={totalViewers} />}
        <PlayerOverlay
          showSeekButtons={!!showBackForwardButtons}
          showInfo={!!showInfoButton}
          infoContent={infoContent}
        />
        <InjectedOverlays playerInstance={playerInstance}>
          {children}
        </InjectedOverlays>
      </PlayerCore>
    </ThemeProvider>
  )
}

export default Player
